import { ChangeEvent, Dispatch, SetStateAction, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import {
    Modal,
    Button,
    CardMedia,
    Input,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Autocomplete,
    Chip,
    Box,
} from "@mui/material";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>;
}

const CATEGORIES = [
    "Footwear", "Apparel", "Electronics", "Accessories", "Appliances", "Toys", "Misc", "Beauty", "Home & Living"
];

const VENDORS = [
    "SportsHub Intl.", "HomeTech Solutions", "Beauty Place", "Fashion House Dubai", "TechGadgets UAE"
];

export default function AddBrand({ open, onClose, setOpen }: ModalProps) {
    const [image, setImage] = useState<string>("");
    const [fileName, setFileName] = useState<string>("");

    const [active, setActive] = useState(true);
    const [isInternalOnly, setIsInternalOnly] = useState(false);

    const [owner, setOwner] = useState("Platform");
    const [vendorName, setVendorName] = useState("");
    const [allowedCategories, setAllowedCategories] = useState<string[]>([]);
    const [restrictedCategories, setRestrictedCategories] = useState<string[]>([]);

    const [internalNotes, setInternalNotes] = useState("");

    return (
        <Modal className="modal rejectReason_modal" open={open} onClose={onClose}>
            <div className="modal-dialog take_action_modal">
                <div className="modal-body">

                    {/* Header */}
                    <div className="modal_title">
                        <h2>Add Brand</h2>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Form */}
                    <form className="form">
                        <div className="gap_p">


                            {/* Upload Image */}
                            <div className="control_group w_100">
                                <label>Brand Logo</label>

                                {image ? (
                                    <div className="upload_image">
                                        <div className="upload_image_holder no_radius v2">
                                            <figure>
                                                <CardMedia component="img" image={image} alt="brand" />
                                            </figure>
                                            <CloseIcon
                                                onClick={() => {
                                                    setImage("");
                                                    setFileName("");
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <label className="upload_image">
                                        <Input
                                            sx={{ display: "none" }}
                                            type="file"
                                            inputProps={{ accept: "image/png,image/jpeg" }}
                                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                const files = event.target.files;
                                                if (files && files[0]) {
                                                    setFileName(files[0].name);
                                                    setImage(URL.createObjectURL(files[0]));
                                                }
                                            }}
                                        />
                                        <span className="upload_image_holder no_radius">
                                            <figure className="add_img">
                                                <img src="/static/images/placeholder.png" alt="upload" />
                                                <span className="upload_img_icon">
                                                    <img src="/static/images/edit2_icon.svg" alt="edit" />
                                                </span>
                                            </figure>
                                        </span>
                                    </label>
                                )}
                            </div>

                            {/* Name */}
                            <div className="control_group w_50">
                                <label htmlFor="brand-name">Brand Name</label>
                                <TextField
                                    placeholder="Enter brand name"
                                    fullWidth
                                    hiddenLabel
                                    inputProps={{ id: "brand-name" }}
                                />
                            </div>

                            {/* Description */}
                            <div className="control_group w_50">
                                <label htmlFor="brand-description">Description</label>
                                <TextField
                                    placeholder="Enter brand description"
                                    fullWidth
                                    hiddenLabel
                                    multiline
                                    rows={4}
                                    inputProps={{ id: "brand-description" }}
                                />
                            </div>




                            {/* Ownership */}
                            <div className="control_group w_50">
                                <label htmlFor="brand-owner">Brand Owner</label>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={owner}
                                        onChange={(e) => setOwner(e.target.value)}
                                        inputProps={{ id: "brand-owner" }}
                                    >
                                        <MenuItem value="Platform">Platform</MenuItem>
                                        <MenuItem value="Vendor">Vendor</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>


                            {/* Associated Vendor (Conditional) */}
                            {owner === "Vendor" && (
                                <div className="control_group w_50">
                                    <label htmlFor="associated-vendor">Associated Vendor</label>
                                    <Autocomplete
                                        id="associated-vendor"
                                        options={VENDORS}
                                        renderInput={(params) => <TextField {...params} placeholder="Search vendor" size="small" />}
                                        value={vendorName}
                                        onChange={(_, newValue) => setVendorName(newValue || "")}
                                    />
                                </div>
                            )}

                            {/* Allowed Categories */}
                            <div className="control_group w_50">
                                <label htmlFor="allowed-categories">Allowed Categories</label>
                                <Autocomplete
                                    id="allowed-categories"
                                    multiple
                                    options={CATEGORIES}
                                    value={allowedCategories}
                                    onChange={(_, newValue) => setAllowedCategories(newValue)}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip label={option} {...getTagProps({ index })} size="small" />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select categories" size="small" />
                                    )}
                                />
                            </div>

                            {/* Restricted Categories */}
                            <div className="control_group w_50">
                                <label htmlFor="restricted-categories">Restricted Categories (Optional)</label>
                                <Autocomplete
                                    id="restricted-categories"
                                    multiple
                                    options={CATEGORIES}
                                    value={restrictedCategories}
                                    onChange={(_, newValue) => setRestrictedCategories(newValue)}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip label={option} {...getTagProps({ index })} size="small" color="error" variant="outlined" />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select restricted categories" size="small" />
                                    )}
                                />
                            </div>

                            <div className="control_group w_100">
                                <label htmlFor="internal-notes">Internal Notes (Admin-only)</label>
                                <TextField
                                    placeholder="Add notes for internal records..."
                                    fullWidth
                                    hiddenLabel
                                    multiline
                                    rows={3}
                                    value={internalNotes}
                                    onChange={(e) => setInternalNotes(e.target.value)}
                                    inputProps={{ id: "internal-notes" }}
                                />
                            </div>
                            <div className="control_group w_100 checkbox_group">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isInternalOnly}
                                            onChange={(e) => setIsInternalOnly(e.target.checked)}
                                        />
                                    }
                                    label="Internal-only (Hidden from Vendors)"
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="footer_btn_flex" style={{ marginTop: '30px' }}>
                            <Button
                                className="btn"
                                variant="outlined"
                                onClick={() => setOpen(false)}
                            >
                                Cancel
                            </Button>

                            <Button
                                className="btn btn_primary"
                                onClick={() => setOpen(false)}
                            >
                                Create Brand
                            </Button>
                        </div>

                    </form>
                </div>
            </div >
        </Modal >
    );
}
